import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, CheckCircle, XCircle, Loader2, Send, Eye, EyeOff, Server, Cloud, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services';

const PROVIDERS = [
  { value: 'azure_acs', label: 'Azure Communication Services', icon: Cloud, color: '#0078d4',
    description: 'Microsoft Azure E-Mail-Dienst' },
  { value: 'office365', label: 'Office 365 (SMTP)', icon: Mail, color: '#d83b01',
    description: 'Microsoft 365 / Exchange Online' },
  { value: 'zoho', label: 'Zoho Mail (SMTP)', icon: Globe, color: '#159f46',
    description: 'Zoho Workplace E-Mail' },
  { value: 'resend', label: 'Resend API', icon: Send, color: '#000',
    description: 'Moderner E-Mail-API-Dienst' },
];

export const EmailConfigPanel = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const [form, setForm] = useState({
    provider: '',
    sender_email: '',
    connection_string: '',
    smtp_user: '',
    smtp_password: '',
    smtp_host: '',
    smtp_port: 587,
    api_key: '',
  });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await adminService.getEmailConfig();
      setConfig(data);
      if (data.configured) {
        setForm(prev => ({
          ...prev,
          provider: data.provider || '',
          sender_email: data.sender_email || '',
          smtp_user: data.smtp_user || '',
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
        }));
      }
    } catch (e) {
      console.error('Error loading email config:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider) => {
    const defaults = {
      office365: { smtp_host: 'smtp.office365.com', smtp_port: 587 },
      zoho: { smtp_host: 'smtp.zoho.eu', smtp_port: 587 },
    };
    setForm(prev => ({
      ...prev,
      provider,
      smtp_host: defaults[provider]?.smtp_host || prev.smtp_host,
      smtp_port: defaults[provider]?.smtp_port || prev.smtp_port,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminService.saveEmailConfig(form);
      toast.success('E-Mail-Konfiguration gespeichert');
      await loadConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Bitte E-Mail-Adresse eingeben'); return; }
    setTesting(true);
    try {
      await adminService.sendTestEmail(testEmail);
      toast.success(`Test-E-Mail an ${testEmail} gesendet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden der Test-E-Mail');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} /></div>;

  const selectedProvider = PROVIDERS.find(p => p.value === form.provider);

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            <Mail className="w-5 h-5" />
            E-Mail-Konfiguration
          </CardTitle>
          <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
            Konfigurieren Sie den E-Mail-Versand für Benachrichtigungen und Passwort-E-Mails
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
            {config?.configured ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">E-Mail-Versand aktiv</p>
                  <p className="text-xs text-green-600">
                    Provider: {PROVIDERS.find(p => p.value === config.provider)?.label || config.provider}
                    {config.sender_email && ` | Absender: ${config.sender_email}`}
                  </p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Nicht konfiguriert</p>
                  <p className="text-xs text-amber-600">Bitte wählen Sie einen E-Mail-Provider und konfigurieren Sie die Zugangsdaten</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Provider Selection */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PROVIDERS.map(p => {
          const Icon = p.icon;
          const isSelected = form.provider === p.value;
          return (
            <Card
              key={p.value}
              className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2' : ''}`}
              style={{
                backgroundColor: 'var(--color-surface)',
                borderColor: isSelected ? p.color : 'var(--color-border)',
                '--tw-ring-color': p.color,
              }}
              onClick={() => handleProviderChange(p.value)}
              data-testid={`email-provider-${p.value}`}
            >
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${p.color}15` }}>
                  <Icon className="w-6 h-6" style={{ color: p.color }} />
                </div>
                <p className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{p.label}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{p.description}</p>
                {isSelected && <Badge className="mt-2 bg-green-50 text-green-700 border-green-200 border">Ausgewählt</Badge>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Configuration Form */}
      {form.provider && (
        <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              {selectedProvider && <selectedProvider.icon className="w-5 h-5" style={{ color: selectedProvider.color }} />}
              {selectedProvider?.label} konfigurieren
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              {/* Sender Email - all providers */}
              <div className="space-y-2">
                <Label>Absender E-Mail-Adresse</Label>
                <Input value={form.sender_email} onChange={e => setForm({...form, sender_email: e.target.value})} placeholder="noreply@glasmatic.de" required data-testid="email-sender" />
              </div>

              {/* Azure ACS fields */}
              {form.provider === 'azure_acs' && (
                <div className="space-y-2">
                  <Label>Connection String</Label>
                  <div className="relative">
                    <Input type={showSecrets ? 'text' : 'password'} value={form.connection_string} onChange={e => setForm({...form, connection_string: e.target.value})}
                      placeholder="endpoint=https://...;accessKey=..." required data-testid="email-azure-connection" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowSecrets(!showSecrets)}>
                      {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Azure Portal &rarr; Communication Services &rarr; Keys</p>
                </div>
              )}

              {/* SMTP fields (Office365, Zoho) */}
              {(form.provider === 'office365' || form.provider === 'zoho') && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Benutzer / E-Mail</Label>
                      <Input value={form.smtp_user} onChange={e => setForm({...form, smtp_user: e.target.value})} placeholder="user@glasmatic.de" required data-testid="email-smtp-user" />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Passwort / App-Passwort</Label>
                      <div className="relative">
                        <Input type={showSecrets ? 'text' : 'password'} value={form.smtp_password} onChange={e => setForm({...form, smtp_password: e.target.value})} placeholder="Passwort" required data-testid="email-smtp-password" />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowSecrets(!showSecrets)}>
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Server</Label>
                      <Input value={form.smtp_host} onChange={e => setForm({...form, smtp_host: e.target.value})} data-testid="email-smtp-host" />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Input type="number" value={form.smtp_port} onChange={e => setForm({...form, smtp_port: parseInt(e.target.value) || 587})} data-testid="email-smtp-port" />
                    </div>
                  </div>
                  {form.provider === 'office365' && (
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      Tipp: Verwenden Sie ein App-Passwort wenn MFA aktiviert ist (Microsoft Account &rarr; Sicherheit &rarr; App-Kennwörter)
                    </p>
                  )}
                </>
              )}

              {/* Resend fields */}
              {form.provider === 'resend' && (
                <div className="space-y-2">
                  <Label>Resend API Key</Label>
                  <div className="relative">
                    <Input type={showSecrets ? 'text' : 'password'} value={form.api_key} onChange={e => setForm({...form, api_key: e.target.value})}
                      placeholder="re_..." required data-testid="email-resend-key" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowSecrets(!showSecrets)}>
                      {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>resend.com &rarr; Dashboard &rarr; API Keys</p>
                </div>
              )}

              <Button type="submit" disabled={saving} className="w-full" data-testid="save-email-config" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)' }}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...</> : 'Konfiguration speichern'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Test Email */}
      {config?.configured && (
        <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Send className="w-5 h-5" />
              Test-E-Mail senden
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@beispiel.de" className="flex-1" data-testid="test-email-input" />
              <Button onClick={handleTest} disabled={testing || !testEmail} data-testid="send-test-email" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)' }}>
                {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sende...</> : <><Send className="w-4 h-4 mr-2" /> Senden</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
